/**
 * Global date-format helpers + monkey-patches.
 *
 * Rescigo standardises every user-facing date on **DD-MM-YYYY** (with hyphens),
 * and every user-facing timestamp on **DD-MM-YYYY HH:mm**.
 *
 * We expose:
 *   • `fmtDate(d)`            — DD-MM-YYYY
 *   • `fmtDateTime(d)`        — DD-MM-YYYY HH:mm
 *   • `fmtTime(d)`            — HH:mm
 *
 * We also monkey-patch `Date.prototype.toLocaleDateString` and
 * `.toLocaleString` at import time so every existing call-site in the
 * codebase (30+ files) automatically switches to DD-MM-YYYY without a
 * repo-wide refactor. Callers that pass locale/options explicitly are
 * respected unchanged. Passing a falsy value or "en-IN" forces DD-MM-YYYY.
 *
 * SAFETY: ISO strings shipped to APIs are unaffected (we only patch
 * DISPLAY methods; `toISOString`, JSON serialisers etc. stay untouched).
 */

const _pad = (n) => String(n).padStart(2, "0");

const _toDate = (d) => {
  if (d instanceof Date) return isNaN(d) ? null : d;
  if (d === null || d === undefined || d === "") return null;
  const dt = new Date(d);
  return isNaN(dt) ? null : dt;
};

export function fmtDate(d) {
  const dt = _toDate(d);
  if (!dt) return "—";
  return `${_pad(dt.getDate())}-${_pad(dt.getMonth() + 1)}-${dt.getFullYear()}`;
}

export function fmtDateTime(d) {
  const dt = _toDate(d);
  if (!dt) return "—";
  return `${fmtDate(dt)} ${_pad(dt.getHours())}:${_pad(dt.getMinutes())}`;
}

export function fmtTime(d) {
  const dt = _toDate(d);
  if (!dt) return "—";
  return `${_pad(dt.getHours())}:${_pad(dt.getMinutes())}`;
}

// ─── Monkey-patch Date display methods ────────────────────────────────
// Preserve the originals in case a caller genuinely needs them.
const _origDateStr = Date.prototype.toLocaleDateString;
const _origFullStr = Date.prototype.toLocaleString;

Date.prototype.toLocaleDateString = function patchedToLocaleDateString(locale, opts) {
  // Explicit locale-with-options → respect caller's intent.
  if (opts && Object.keys(opts).length > 0) {
    return _origDateStr.call(this, locale, opts);
  }
  return fmtDate(this);
};

Date.prototype.toLocaleString = function patchedToLocaleString(locale, opts) {
  if (opts && Object.keys(opts).length > 0) {
    return _origFullStr.call(this, locale, opts);
  }
  return fmtDateTime(this);
};
